import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Mail, Phone, Building2, Calendar, X, Loader2, Check } from "lucide-react";
import STLogo from "@/components/STLogo";
import { useAuth } from "@/admin/AuthProvider";
import NoIndex from "@/admin/NoIndex";
import { STATUS_OPTIONS, statusMeta } from "@/admin/status";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSubmissions() {
  const { user, token, logout, verifying } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: { new: 0, in_review: 0, replied: 0, closed: 0 } });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/contact/submissions?limit=200`, { headers: authHeader }),
        axios.get(`${API}/contact/submissions/stats`, { headers: authHeader }),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Unable to load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== "all" && (it.status || "new") !== statusFilter) return false;
      if (!q) return true;
      return [it.name, it.company, it.email, it.subject, it.message].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [items, query, statusFilter]);

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await axios.patch(
        `${API}/contact/submissions/${id}/status`,
        { status: newStatus },
        { headers: authHeader }
      );
      const updated = res.data;
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...updated } : it)));
      if (selected?.id === id) setSelected((s) => ({ ...s, ...updated }));
      // Optimistic stats update
      setStats((prev) => {
        const by = { ...prev.by_status };
        const prevStatus = items.find((it) => it.id === id)?.status || "new";
        if (by[prevStatus] !== undefined) by[prevStatus] = Math.max(0, by[prevStatus] - 1);
        by[newStatus] = (by[newStatus] || 0) + 1;
        return { ...prev, by_status: by };
      });
      toast.success(`Marked as ${statusMeta(newStatus).label}.`);
    } catch (e) {
      toast.error("Unable to update status.");
    }
  };

  if (verifying) {
    return (<div className="min-h-screen flex items-center justify-center bg-surface-muted"><Loader2 size={20} className="animate-spin text-ink-secondary" /></div>);
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const onLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <main data-testid="admin-submissions-page" className="min-h-screen bg-surface-muted">
      <NoIndex />
      <header className="bg-white border-b border-line">
        <div className="container-pro py-4 flex items-center justify-between gap-4">
          <a href="/" className="inline-flex" data-testid="admin-home-link"><STLogo /></a>
          <div className="hidden md:flex items-center gap-3 text-xs text-ink-secondary">
            <span className="font-mono uppercase tracking-widest text-ink-muted">// admin</span>
            <span>{user.email}</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-ink-secondary hover:text-ink-primary border border-line rounded-md">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <section className="container-pro py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="overline">// Inbox</div>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl font-medium tracking-tight">Contact submissions</h1>
            <p className="mt-2 text-sm text-ink-secondary">
              {stats.total} total
              {query && ` · ${filtered.length} match "${query}"`}
              {statusFilter !== "all" && ` · filtered by ${statusMeta(statusFilter).label}`}
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-ink-muted pointer-events-none" />
              <input
                type="text" placeholder="Search name, company, email…"
                value={query} onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search"
                className="w-full bg-white border border-line rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red"
              />
            </div>
            <button onClick={fetchAll} data-testid="admin-refresh" className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-ink-secondary hover:text-ink-primary border border-line rounded-md bg-white" aria-label="Refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          <FilterChip
            active={statusFilter === "all"} onClick={() => setStatusFilter("all")}
            label="All" count={stats.total} testid="filter-all"
          />
          {STATUS_OPTIONS.map((opt) => (
            <FilterChip
              key={opt.value}
              active={statusFilter === opt.value}
              onClick={() => setStatusFilter(opt.value)}
              label={opt.label} count={stats.by_status[opt.value] || 0}
              dot={opt.dot}
              testid={`filter-${opt.value}`}
            />
          ))}
        </div>

        {loading ? (
          <SkeletonList />
        ) : filtered.length === 0 ? (
          <EmptyState query={query} statusFilter={statusFilter} />
        ) : (
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-surface-muted border-b border-line font-mono text-[10px] uppercase tracking-widest text-ink-muted">
              <div className="col-span-3">From</div>
              <div className="col-span-2">Company</div>
              <div className="col-span-2">Subject</div>
              <div className="col-span-2">Received</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Email</div>
            </div>
            <ul className="divide-y divide-line">
              {filtered.map((s) => (
                <li
                  key={s.id}
                  onClick={() => setSelected(s)}
                  data-testid="admin-submission-row"
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 hover:bg-surface-muted cursor-pointer transition-colors"
                >
                  <div className="md:col-span-3">
                    <div data-testid="admin-submission-name" className="font-heading text-sm font-semibold text-ink-primary">{s.name}</div>
                    <div className="text-xs text-ink-muted truncate">{s.email}</div>
                  </div>
                  <div className="md:col-span-2 text-sm text-ink-secondary truncate">{s.company}</div>
                  <div className="md:col-span-2 text-sm text-ink-secondary truncate">{s.subject}</div>
                  <div className="md:col-span-2 text-xs text-ink-secondary font-mono">{formatDate(s.submitted_at)}</div>
                  <div className="md:col-span-2"><StatusPill status={s.status || "new"} /></div>
                  <div className="md:col-span-1 flex items-center gap-1">
                    <Pulse ok={s.email_sent_to_sales} title="Sales notified" />
                    <Pulse ok={s.email_sent_to_customer} title="Customer ack" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {selected && (
        <DetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onChangeStatus={(newStatus) => updateStatus(selected.id, newStatus)}
        />
      )}
    </main>
  );
}

const formatDate = (iso) => {
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso); }
};

const FilterChip = ({ active, onClick, label, count, dot, testid }) => (
  <button
    type="button" onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
      active ? "bg-brand-black text-white border-brand-black" : "bg-white text-ink-secondary border-line hover:border-brand-black"
    }`}
  >
    {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
    {label}
    <span className={`font-mono text-[10px] ${active ? "text-white/60" : "text-ink-muted"}`}>{count}</span>
  </button>
);

const StatusPill = ({ status }) => {
  const m = statusMeta(status);
  return (
    <span data-testid={`status-pill-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded border ${m.chip}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.short}
    </span>
  );
};

const Pulse = ({ ok, title }) => (
  <span title={title} aria-label={title} className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
);

const SkeletonList = () => (
  <div className="bg-white border border-line rounded-xl p-6 space-y-3">
    {[...Array(4)].map((_, i) => <div key={i} className="h-14 rounded bg-surface-muted animate-pulse" />)}
  </div>
);

const EmptyState = ({ query, statusFilter }) => (
  <div className="bg-white border border-line rounded-xl p-12 text-center" data-testid="admin-empty-state">
    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-surface-muted text-ink-muted"><Mail size={20} /></div>
    <h3 className="mt-5 font-heading text-lg font-medium">
      {query || statusFilter !== "all" ? "No matches" : "No inquiries yet"}
    </h3>
    <p className="mt-1 text-sm text-ink-secondary">
      {query
        ? "Try a different search term."
        : statusFilter !== "all"
        ? `No submissions with status "${statusMeta(statusFilter).label}".`
        : "When visitors submit the contact form, you'll see them listed here."}
    </p>
  </div>
);

const DetailDrawer = ({ item, onClose, onChangeStatus }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div data-testid="admin-detail-drawer" className="fixed inset-0 z-50 flex">
      <button onClick={onClose} aria-label="Close" className="flex-1 bg-black/50" data-testid="admin-detail-backdrop" />
      <aside className="w-full max-w-xl bg-white border-l border-line h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">REF · {item.id}</div>
          <button onClick={onClose} data-testid="admin-detail-close" className="p-1 text-ink-muted hover:text-ink-primary">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 md:p-8">
          <h2 className="font-heading text-2xl font-semibold text-ink-primary">{item.subject}</h2>
          <div className="mt-1 text-sm text-ink-muted font-mono">{formatDate(item.submitted_at)}</div>

          {/* Status workflow */}
          <div className="mt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-secondary mb-2">Status</div>
            <div className="flex flex-wrap gap-2" data-testid="status-workflow">
              {STATUS_OPTIONS.map((opt) => {
                const active = (item.status || "new") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => !active && onChangeStatus(opt.value)}
                    data-testid={`status-btn-${opt.value}`}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      active
                        ? "bg-brand-black text-white border-brand-black cursor-default"
                        : "bg-white text-ink-secondary border-line hover:border-brand-black"
                    }`}
                  >
                    {active && <Check size={12} />}
                    <span className={`h-1.5 w-1.5 rounded-full ${opt.dot}`} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {item.status_updated_at && (
              <div className="mt-2 text-[11px] text-ink-muted font-mono">
                Last updated {formatDate(item.status_updated_at)}
                {item.status_updated_by ? ` · ${item.status_updated_by}` : ""}
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4">
            <Detail icon={<Building2 size={14} />} label="Company" value={item.company} />
            <Detail icon={<Mail size={14} />} label="Email" value={
              <a href={`mailto:${item.email}`} className="hover:text-brand-red transition-colors" data-testid="admin-detail-email">{item.email}</a>
            } />
            <Detail icon={<Phone size={14} />} label="Phone" value={
              <a href={`tel:${item.phone}`} className="hover:text-brand-red transition-colors">{item.phone}</a>
            } />
            <Detail icon={<Calendar size={14} />} label="Submitted" value={formatDate(item.submitted_at)} />
          </div>

          <div className="mt-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-secondary mb-2">Message</div>
            <div className="text-sm text-ink-primary whitespace-pre-wrap leading-relaxed border-l-2 border-brand-red ps-4">{item.message}</div>
          </div>

          <div className="mt-8 flex items-center gap-3 text-xs">
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${item.email_sent_to_sales ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-700"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${item.email_sent_to_sales ? "bg-emerald-500" : "bg-amber-500"}`} />
              Sales {item.email_sent_to_sales ? "notified" : "not sent"}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${item.email_sent_to_customer ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-700"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${item.email_sent_to_customer ? "bg-emerald-500" : "bg-amber-500"}`} />
              Customer {item.email_sent_to_customer ? "ack" : "not sent"}
            </span>
          </div>

          <div className="mt-10 flex gap-3">
            <a
              href={`mailto:${item.email}?subject=Re: ${encodeURIComponent(item.subject)}&body=${encodeURIComponent(`Hello ${item.name.split(" ")[0]},\n\nThank you for contacting Smart Terminals.\n\n`)}`}
              className="btn-primary"
              data-testid="admin-detail-reply"
              onClick={() => {
                if ((item.status || "new") === "new") onChangeStatus("replied");
              }}
            >
              Reply by email
            </a>
            <button onClick={onClose} className="btn-ghost" data-testid="admin-detail-cancel">Close</button>
          </div>
        </div>
      </aside>
    </div>
  );
};

const Detail = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-ink-secondary border border-line mt-0.5">{icon}</span>
    <div className="min-w-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="mt-0.5 text-sm text-ink-primary break-words">{value}</div>
    </div>
  </div>
);
